import { Button, Icon, Tooltip } from 'antd';
import cn from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import styles from './PinnedPanel.module.scss';

export const PinnedPanel = forwardRef(
  (
    {
      children,
      className,
      defaultPinned = true,
      header,
      position = 'sticky',
      pinTooltipTitle = 'Закрепить панель',
      unpinTooltipTitle = 'Открепить панель',
      pinIcon = 'pushpin',
      unpinIcon = 'double-left',
      renderPinAction,
      onPinnedChange,
      onOverlayVisibleChange,
    },
    ref
  ) => {
    const [isPinned, setIsPinned] = useState(defaultPinned);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [closeOverlaysKey, setCloseOverlaysKey] = useState(0);
    const isPinnedRef = useRef(defaultPinned);

    const handleOverlayVisibleChange = useCallback(
      (visible) => {
        setIsOverlayOpen(visible);

        if (typeof onOverlayVisibleChange === 'function') {
          onOverlayVisibleChange(visible);
        }
      },
      [onOverlayVisibleChange]
    );

    const closePanelOverlays = useCallback(() => {
      handleOverlayVisibleChange(false);
      setCloseOverlaysKey((prevKey) => prevKey + 1);
    }, [handleOverlayVisibleChange]);

    const updatePinned = useCallback(
      (nextPinned) => {
        if (isPinnedRef.current === nextPinned) {
          return;
        }

        isPinnedRef.current = nextPinned;
        setIsPinned(nextPinned);

        if (!nextPinned) {
          closePanelOverlays();
        }

        if (typeof onPinnedChange === 'function') {
          onPinnedChange(nextPinned);
        }
      },
      [closePanelOverlays, onPinnedChange]
    );

    const unpin = useCallback(() => updatePinned(false), [updatePinned]);
    const togglePinned = useCallback(
      () => updatePinned(!isPinnedRef.current),
      [updatePinned]
    );

    useImperativeHandle(
      ref,
      () => ({
        closeOverlays: closePanelOverlays,
        setPinned: updatePinned,
        togglePinned,
        unpin,
      }),
      [closePanelOverlays, togglePinned, unpin, updatePinned]
    );

    const defaultPinAction = (
      <Tooltip title={isPinned ? unpinTooltipTitle : pinTooltipTitle}>
        <Button
          htmlType="button"
          className={styles.pinButton}
          aria-label={isPinned ? unpinTooltipTitle : pinTooltipTitle}
          aria-expanded={isPinned}
          onClick={togglePinned}
        >
          <Icon type={isPinned ? unpinIcon : pinIcon} />
        </Button>
      </Tooltip>
    );

    const headerAction =
      typeof renderPinAction === 'function'
        ? renderPinAction({
            defaultAction: defaultPinAction,
            isPinned,
            togglePinned,
            pinTooltipTitle,
            unpinTooltipTitle,
          })
        : defaultPinAction;

    const childProps = {
      closeOverlays: closePanelOverlays,
      closeOverlaysKey,
      isOverlayOpen,
      isPinned,
      onOverlayVisibleChange: handleOverlayVisibleChange,
      setPinned: updatePinned,
      togglePinned,
      unpin,
    };

    return (
      <div
        className={cn(
          styles.root,
          styles[position],
          {
            [styles.pinned]: isPinned,
            [styles.unpinned]: !isPinned,
          },
          className
        )}
      >
        <div className={styles.content}>
          <div className={styles.header}>
            {header && (
              <div className={styles.headerContent} hidden={!isPinned}>
                {header}
              </div>
            )}
            {headerAction}
          </div>
          <div className={styles.body} hidden={!isPinned}>
            {typeof children === 'function' ? children(childProps) : children}
          </div>
        </div>
      </div>
    );
  }
);

PinnedPanel.displayName = 'PinnedPanel';
